import { DependencyList, useCallback, useEffect, useState } from 'react'
import { ApiError } from './api'

export type Load<T> =
  | { state: 'loading'; retry: () => void }
  | { state: 'error'; error: ApiError; retry: () => void }
  | { state: 'ready'; data: T; retry: () => void; setData: (data: T) => void }

/** Load data for a page, with loading and error states and a Try again */
export function useLoad<T>(fetcher: (signal: AbortSignal) => Promise<T>, deps: DependencyList): Load<T> {
  const [attempt, setAttempt] = useState(0)
  const [result, setResult] = useState<
    { state: 'loading' } | { state: 'error'; error: ApiError } | { state: 'ready'; data: T }
  >({ state: 'loading' })

  useEffect(() => {
    const controller = new AbortController()
    setResult({ state: 'loading' })
    fetcher(controller.signal)
      .then((data) => setResult({ state: 'ready', data }))
      .catch((error) => {
        if (error?.name === 'AbortError') return
        setResult({ state: 'error', error: error instanceof ApiError ? error : new ApiError(0, String(error)) })
      })
    return () => controller.abort()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, attempt])

  const retry = useCallback(() => setAttempt((n) => n + 1), [])
  const setData = useCallback((data: T) => setResult({ state: 'ready', data }), [])
  if (result.state === 'ready') return { ...result, retry, setData }
  return { ...result, retry }
}
